import { QueryClient } from '@tanstack/react-query';
import type { LeadStatus, LeadPriority, LeadType } from '@/types/database';

// Check if an error is an AbortError (query was cancelled)
function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/**
 * Check if an error is a connection/network error that might resolve on retry.
 * These are common during dev server restarts.
 */
function isConnectionError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    error.name === 'TypeError' || // Network errors often manifest as TypeError
    message.includes('fetch') ||
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('connection') ||
    message.includes('failed to fetch') ||
    message.includes('load failed')
  );
}

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000, // 1 minute
        refetchOnWindowFocus: false,
        // Refetch on reconnect to recover from connection issues
        refetchOnReconnect: true,
        // Retry logic:
        // - Don't retry AbortErrors (React Strict Mode re-renders)
        // - Retry connection errors up to 3 times with exponential backoff
        // - Retry other errors once
        retry: (failureCount, error) => {
          if (isAbortError(error)) {
            return false;
          }
          if (isConnectionError(error)) {
            return failureCount < 3;
          }
          return failureCount < 1;
        },
        retryDelay: (attemptIndex, error) => {
          // Use exponential backoff for connection errors
          if (isConnectionError(error)) {
            return Math.min(1000 * 2 ** attemptIndex, 10000);
          }
          return 1000;
        },
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

import type { ActivityFilters } from '@/lib/activity/types';

// Query key factory for consistent cache keys
export const queryKeys = {
  leads: {
    all: ['leads'] as const,
    list: (filters?: LeadFilters) => ['leads', 'list', filters] as const,
    detail: (id: string) => ['leads', 'detail', id] as const,
  },
  contacts: {
    all: ['contacts'] as const,
    list: () => ['contacts', 'list'] as const,
    detail: (id: string) => ['contacts', 'detail', id] as const,
  },
  tasks: {
    all: ['tasks'] as const,
    byLead: (leadId: string) => ['tasks', 'lead', leadId] as const,
    upcoming: () => ['tasks', 'upcoming'] as const,
  },
  dashboard: {
    stats: ['dashboard', 'stats'] as const,
    activity: ['dashboard', 'activity'] as const,
  },
  activity: {
    all: ['activity'] as const,
    list: (filters?: ActivityFilters, limit?: number, offset?: number) =>
      ['activity', 'list', filters, limit, offset] as const,
    dashboard: (limit: number) => ['activity', 'dashboard', limit] as const,
  },
  offices: {
    all: ['offices'] as const,
  },
  users: {
    all: ['users'] as const,
    list: () => ['users', 'list'] as const,
  },
  search: {
    all: ['search'] as const,
    global: (query: string, limit?: number) => ['search', 'global', query, limit] as const,
  },
} as const;

// Filter types for queries
export interface LeadFilters {
  status?: LeadStatus | 'all';
  priority?: LeadPriority | 'all';
  leadType?: LeadType | 'all';
  officeId?: string;
  search?: string;
}
